/**
 * Utility functions for generating standardized Firestore document IDs
 *
 * Standard format: <type>-<name>
 * - Lowercase
 * - Hyphens for spaces
 * - No special characters except hyphens
 *
 * Examples:
 * - origin-human-origin
 * - knack-astrogation
 * - effect-dr-1-blunt
 */

/**
 * Generate a standard ID from a type and name
 * @param type The document type (e.g., 'origin', 'knack', 'effect')
 * @param name The document name (e.g., 'Human Origin', 'Astrogation')
 * @returns Standardized ID in format: type-name
 */
export function generateStandardId(type: string, name: string): string {
	// Convert to lowercase
	let cleanType = type.toLowerCase();
	let cleanName = name.toLowerCase();

	// Remove special characters (except spaces and hyphens)
	cleanType = cleanType.replace(/[^a-z0-9\s-]/g, '');
	cleanName = cleanName.replace(/[^a-z0-9\s-]/g, '');

	// Replace spaces with hyphens
	cleanType = cleanType.replace(/\s+/g, '-');
	cleanName = cleanName.replace(/\s+/g, '-');

	// Replace multiple consecutive hyphens with single hyphen
	cleanType = cleanType.replace(/-+/g, '-');
	cleanName = cleanName.replace(/-+/g, '-');

	// Trim hyphens from start and end
	cleanType = cleanType.replace(/^-+|-+$/g, '');
	cleanName = cleanName.replace(/^-+|-+$/g, '');

	return `${cleanType}-${cleanName}`;
}

/**
 * Generate a standard ID for a property
 * @param propertyType The property type (e.g., 'origin', 'legacy')
 * @param name The property name
 * @returns Standardized property ID
 */
export function generatePropertyId(propertyType: string, name: string): string {
	return generateStandardId(propertyType, name);
}

/**
 * Generate a standard ID for a blueprint feature
 * @param blueprintType The blueprint type (e.g., 'character')
 * @param rank The rank number (0-10)
 * @param name The feature name
 * @returns Standardized feature ID (e.g., 'character-0-origin')
 */
export function generateBlueprintFeatureId(blueprintType: string, rank: number, name: string): string {
	const cleanName = name.toLowerCase()
		.replace(/[^a-z0-9\s-]/g, '')
		.replace(/\s+/g, '-')
		.replace(/-+/g, '-')
		.replace(/^-+|-+$/g, '');

	return `${blueprintType.toLowerCase()}-${rank}-${cleanName}`;
}

/**
 * Generate a standard ID for an effect
 * @param effectType The effect type (e.g., 'dr', 'lookup')
 * @param name The effect name
 * @returns Standardized effect ID
 */
export function generateEffectId(effectType: string, name: string): string {
	return generateStandardId(effectType, name);
}
